// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a viewer's colours for each dataset, and how two copies of them
 * become one: this browser's and the signed-in account's
 * (`displayPrefsSync.ts`, `functions/api/account/display.ts`). Shapes
 * only: no DOM, imported by the browser and by Pages Functions.
 *
 * Only what the viewer changed is kept, field by field: a palette, the
 * inversion, colour limits (owner, 2026-10-09: "any changes by a user to
 * a dataset min max palette limits should be recorded and remembered").
 * A dataset left alone keeps following its default, so a later change to
 * a shared standard (`displayGroups.ts`) still reaches it. "Reset colours"
 * leaves a marker (`reset: true`) so a device that still has the old
 * colours does not bring them back; for every dataset the newest entry
 * wins (the way playlists merged until they retired, 2026-11-03). Markers are dropped after
 * {@link RESET_DAYS}.
 */

import { PALETTE_IDS, type PaletteId } from './paletteIds'

/** Most datasets one person keeps colours for. */
export const MAX_DISPLAY_PREFS = 1000
/** Largest stored set, in bytes of JSON. */
export const DISPLAY_PREFS_MAX_BYTES = 200_000
/** How long a reset is remembered, so every device hears of it. */
export const RESET_DAYS = 180

/** One dataset's colours, as far as the viewer changed them. */
export interface DisplayPref {
  palette?: PaletteId
  inverted?: boolean
  /** Physical values in the dataset's units, on their own axis. */
  limits?: { min: number; max: number; scale: 'linear' | 'log' }
  /** A reset: back to the dataset's default, and nothing else set. */
  reset?: true
  /** ISO time of the change. */
  updatedAt: string
}

export interface DisplayPrefsDoc {
  /** By dataset id (`R2_DASH_…`), which a publisher's renaming leaves alone. */
  prefs: Record<string, DisplayPref>
}

const isIso = (v: unknown): v is string => typeof v === 'string' && v.length <= 40 && !Number.isNaN(Date.parse(v))

function sanitizeLimits(raw: unknown): DisplayPref['limits'] | null {
  if (!raw || typeof raw !== 'object') return null
  const l = raw as Record<string, unknown>
  const { min, max } = l
  if (typeof min !== 'number' || typeof max !== 'number' || !Number.isFinite(min) || !Number.isFinite(max) || !(min < max)) return null
  if (l.scale !== 'linear' && l.scale !== 'log') return null
  if (l.scale === 'log' && !(min > 0)) return null
  return { min, max, scale: l.scale }
}

/** One stored entry, or null when it is not one. Unknown fields are dropped. */
export function sanitizePref(raw: unknown): DisplayPref | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (!isIso(r.updatedAt)) return null
  if (r.reset === true) return { reset: true, updatedAt: r.updatedAt }
  const out: DisplayPref = { updatedAt: r.updatedAt }
  if (typeof r.palette === 'string' && (PALETTE_IDS as readonly string[]).includes(r.palette)) out.palette = r.palette as PaletteId
  if (typeof r.inverted === 'boolean') out.inverted = r.inverted
  const limits = sanitizeLimits(r.limits)
  if (limits) out.limits = limits
  return out.palette !== undefined || out.inverted !== undefined || out.limits ? out : null
}

/** A request body or stored value as a {@link DisplayPrefsDoc}; null when it is not one. */
export function sanitizeDisplayPrefsDoc(raw: unknown): DisplayPrefsDoc | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (!r.prefs || typeof r.prefs !== 'object' || Array.isArray(r.prefs)) return null
  const prefs: Record<string, DisplayPref> = {}
  let n = 0
  for (const [id, value] of Object.entries(r.prefs as Record<string, unknown>)) {
    if (n >= MAX_DISPLAY_PREFS) break
    if (!id || id.length > 200) continue
    const pref = sanitizePref(value)
    if (pref) {
      prefs[id] = pref
      n++
    }
  }
  return { prefs }
}

/**
 * One set from two: for every dataset, the newest entry wins (a tie keeps
 * `local`'s). Resets older than {@link RESET_DAYS} at `nowMs` are forgotten;
 * past {@link MAX_DISPLAY_PREFS}, the oldest entries go.
 */
export function mergeDisplayPrefsDocs(local: DisplayPrefsDoc, remote: DisplayPrefsDoc, nowMs: number): DisplayPrefsDoc {
  const forgetBefore = nowMs - RESET_DAYS * 86_400_000
  const chosen = new Map<string, DisplayPref>()
  for (const [id, p] of [...Object.entries(remote.prefs), ...Object.entries(local.prefs)]) {
    const current = chosen.get(id)
    if (!current || Date.parse(p.updatedAt) >= Date.parse(current.updatedAt)) chosen.set(id, p)
  }
  const kept = [...chosen].filter(([, p]) => !(p.reset && Date.parse(p.updatedAt) < forgetBefore))
  kept.sort(([a, x], [b, y]) => Date.parse(y.updatedAt) - Date.parse(x.updatedAt) || a.localeCompare(b))
  return { prefs: Object.fromEntries(kept.slice(0, MAX_DISPLAY_PREFS).sort(([a], [b]) => a.localeCompare(b))) }
}

/** Whether two sets hold the same entries. */
export function sameDisplayPrefsDoc(a: DisplayPrefsDoc, b: DisplayPrefsDoc): boolean {
  const norm = (d: DisplayPrefsDoc) => JSON.stringify(Object.entries(d.prefs).sort(([x], [y]) => x.localeCompare(y)))
  return norm(a) === norm(b)
}
